import React from 'react';

function makeCancelable<K>(promise: Promise<K>) {
    let isCanceled = false;
    const wrappedPromise = new Promise<K>((resolve, reject) => {
        promise
            .then((val) => (isCanceled ? reject({ isCanceled }) : resolve(val)))
            .catch((error) => (isCanceled ? reject({ isCanceled }) : reject(error)));
    });
    return {
        promise: wrappedPromise,
        cancel() {
            isCanceled = true;
        },
    };
}

type CancelablePromiseType = ReturnType<typeof makeCancelable>;

export function useCancelOnUnmount() {
    const promises = React.useRef<CancelablePromiseType[]>([]);

    // useEffect cleans up by calling cancel on every stored promise.
    React.useEffect(() => {
        // The effect has no dependencies so the cleanup is effectively done when the component unmounts
        return function cancel() {
            promises.current.forEach((p) => p.cancel());
            promises.current = [];
        };
    }, []);

    // cancelablePromise remembers the promises that you
    // have called so far. It returns a wrapped cancelable
    // promise
    function cancelOnUnmount<K>(p: Promise<K>) {
        const cPromise = makeCancelable(p);
        promises.current.push(cPromise);
        return cPromise.promise;
    }
    return [cancelOnUnmount];
}
